(ns frontend.view.grid.indirect-holdings-grid.events
  (:require
   [frontend.view.grid.indirect-holdings-grid.db :as db]
   [frontend.view.grid.indirect-holdings-grid.columns :as columns]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.local-storage :as ls]
   [frontend.view.grid.companies-grid.events :as companies-events]
   [frontend.view.grid.real-estates-grid.events :as real-estates-events]
   [frontend.view.grid.fof-assets-grid.events :as fof-assets-events]
   [frontend.view.grid.other-assets-grid.events :as other-assets-events]
   [common.localization :as loc]
   [common.util :as cu]
   [frontend.view.modals.events :as modals]
   [potpuri.core :refer [index-by fn-> fn->>]]
   [frontend.state.current-session :as session]
   [frontend.actions.named-view :as named-view-actions]
   [common.schema.registry :as registry]
   [frontend.logging :as logging]
   [frontend.utils :as fu]
   [frontend.view.modals.confirmation.view :as confirmation]
   [clojure.data :as data]
   [reagent.core :as r]
   [frontend.view.entity :as f-entity]
   [common.schema.entity-types :as entity-types]
   [common.schema.associations :as associations]
   [common.schema.assets :as assets]))

(defn ls-path
  [& rest]
  (concat
   [(:db/id @session/account)
    (:db/id @session/tenant)
    :indirect-holdings-view-settings]
   rest))

(defn precalculated-schema
  [recalculate?]
  (let [tenant-schema @session/tenant-schema
        calculated @db/schema
        schema
        (cond
          (and (not recalculate?)
               calculated)
          calculated
          :else
          (->>
           (registry/get-schema @session/tenant-schema :entity.type/calculated-indirect-holding)
           (filter (fn [[k _]]
                     (= "indirect-holding" (namespace k))))
           (sort-by (fn [[k _]]
                      (let [fs (registry/get-field-schema tenant-schema k)]
                        (loc/field-name k fs))))))
        asset-types (session/holding-target-asset-types)
        allowed-asset-types (set asset-types)
        asset-type-schema (fn [entity-type]
                            (when (and (some #{entity-type} allowed-asset-types)
                                       (or recalculate?
                                           (not calculated)))
                              (->> (case entity-type
                                     :entity.type/company
                                     (companies-events/precalculated-schema recalculate?)
                                     :entity.type/real-estate
                                     (real-estates-events/precalculated-schema recalculate?)
                                     :entity.type/fof-asset
                                     (fof-assets-events/precalculated-schema recalculate?)
                                     :entity.type/other-asset
                                     (other-assets-events/precalculated-schema recalculate?)
                                     nil))))
        fund-schema (->> (fof-assets-events/precalculated-schema recalculate?)
                         (remove (fn [[k _]]
                                   (= k :asset/name)))
                         (sort-by (fn [[k _]]
                                    (let [fs (registry/get-field-schema tenant-schema k)]
                                      (loc/field-name k fs))))
                         (map (fn [[k v]]
                                [[:indirect-holding/fund k] v]))
                         doall)
        target-asset-schema (->> asset-types
                                 (mapcat asset-type-schema)
                                 (remove (fn [[k _]]
                                           (= k :asset/name)))
                                 (sort-by
                                  (fn [[k _]]
                                    (let [fs (registry/get-field-schema tenant-schema k)]
                                      (loc/field-name k fs))))
                                 (map (fn [[k v]]
                                        [[:indirect-holding/holding-target k] v]))
                                 doall)
        schema (if (and (not recalculate?)
                        calculated)
                 schema
                 (->> (concat schema fund-schema target-asset-schema)
                      (group-by first)
                      (map (fn [[k opts]]
                             (first opts)))
                      (sort-by (fn [[k _]]
                                 (let [fs (registry/get-field-schema tenant-schema k)]
                                   (loc/field-name k fs))))
                      (doall)))]
    (when (or recalculate?
              (not calculated))
      (reset! db/schema schema))
    schema))

;;precalculate for performance reasons
(defn keys-and-schemas
  []
  (precalculated-schema false))

(defn indirect-holding-column-keys
  []
  (let [keys-schemas (keys-and-schemas)
        column-keys (->> keys-schemas
                         (map first))]
    ;; indirect-holdings are calculated entities => not adding :db/id
    column-keys))

(def default-kanban-attributes
  [[:indirect-holding/holding-target :asset/country]
   [:indirect-holding/holding-target :asset/city]
   [:indirect-holding/holding-target :asset/description]])

(defn visible-keys-and-schemas
  []
  (let [{:keys [attributes footer-1 footer-2]} @db/card-settings
        visible-attributes (set (concat @db/selected-columns
                                        attributes
                                        default-kanban-attributes
                                        [footer-1 footer-2
                                         (entity-types/type->owner-attribute
                                          :entity.type/calculated-indirect-holding)]))]
    (->> (keys-and-schemas)
         (filter (fn [[k fs]]
                   (some #{k} visible-attributes))))))

(declare expanded? toggle-expand!)

(defn column-tabs
  []
  "Returns all the tabs that should be visible in field selector"
  (let [target-asset-types (session/holding-target-asset-types)]
    (concat [{:key   "indirect-holding-key"
              :label (loc/tr :indirect-holding)}]
            ;; fof-asset having the target-asset (which could also be a fof-asset) as holding:
            [{:key   :investor-fund
              :label (loc/tr :investor-fund)}]
            (->> (concat (when (> (count target-asset-types) 1)
                           [:entity.type/asset])
                         target-asset-types)
                 (map (fn [entity-type]
                        {:key   entity-type
                         :label (loc/tr :entity-type-fields [(loc/tr entity-type)])}))))))

(defn resolve-tab
  [field]
  (let [target-asset-types (session/holding-target-asset-types)
        ref-field? (and (vector? field)
                        (keyword? (first field))
                        (keyword? (second field)))
        [ref attr] (when ref-field?
                     field)]
    (cond
      (keyword? field)
      "indirect-holding-key"

      (and ref-field?
           (= ref :indirect-holding/fund))
      :investor-fund

      (and ref-field?
           (= (count target-asset-types) 1))
      (first target-asset-types)

      (and ref-field?
           (= (namespace attr) "asset"))
      :entity.type/asset

      (and ref-field?
           (= (namespace attr) "common"))
      :entity.type/asset

      (and ref-field?
           (= (namespace attr) "company"))
      :entity.type/company

      (and ref-field?
           (= (namespace attr) "real-estate"))
      :entity.type/real-estate

      (and ref-field?
           (= (namespace attr) "fof-asset"))
      :entity.type/fof-asset

      (and ref-field?
           (= (namespace attr) "other-asset"))
      :entity.type/other-asset

      :else
      :unknown)))

;;necessary arguments to use common grid functions
(defn setup-args
  []
  {:state db/indirect-holdings
   :entity-type :entity.type/calculated-indirect-holding
   :selected-columns-state db/selected-columns
   :ordered-columns-state db/ordered-columns
   :filters-state db/indirect-holding-filters
   :view-settings-state db/view-settings
   :column-widths-state db/column-widths
   :ls-path ls-path
   :kanban? true
   :kanban-args {:default-grouped-by  :indirect-holding/fund
                 :kanban-state        db/kanban
                 :grouped-by-state    db/grouped-by
                 :card-settings-state db/card-settings
                 ;;for long card view:
                 :default-attributes  default-kanban-attributes
                 :expanded-state      db/expanded
                 :sorted-kanban-entities-state db/sorted-kanban-entities
                 :lists-state         db/lists
                 :expanded?           expanded?
                 :toggle-expand!      toggle-expand!}
   :column-tabs (column-tabs)
   :resolve-tab resolve-tab

   :column-keys indirect-holding-column-keys
   :default-columns columns/default-columns
   :mandatory? columns/mandatory?
   :visible-keys-and-schemas-fn visible-keys-and-schemas})

(defn expanded?
  [entity-id]
  (let [card-mode (or (:card-mode @db/kanban)
                      :short-card)
        individual (get @db/expanded entity-id)]
    (or (and (= card-mode :short-card)
             individual)
        (and (= card-mode :long-card)
             (not individual)))))

(defn toggle-expand!
  [entity-id]
  (let [expanded? (get @db/expanded entity-id)]
    (if expanded?
      (swap! db/expanded dissoc entity-id)
      (swap! db/expanded assoc entity-id true))))

(defn resolve-column-key-universe
  []
  (grid/resolve-column-key-universe (setup-args)))

(defn resolve-column-keys
  []
  (grid/resolve-column-keys (setup-args)))

(defn cleanup
  []
  (when-let [tracker (:tracker-1 @db/indirect-holdings)]
    (r/dispose! tracker))
  (when-let [tracker (:tracker-2 @db/indirect-holdings)]
    (r/dispose! tracker))
  (when-let [tracker (:tracker-3 @db/indirect-holdings)]
    (r/dispose! tracker))

  (swap! db/indirect-holdings dissoc
    :loaded
    :schema
    :entities
    :entity-types
    :view-settings
    :sorted-grid-entities
    :search
    :selected
    :grid-entities))

(defn setup-column-state!
  [view-settings]
  (reset! db/view-settings view-settings)
  (when (nil? @db/selected-columns)
    (reset! db/selected-columns columns/default-columns)
    (ls/update-in (ls-path) assoc :selected-columns columns/default-columns))
  (grid/recalculate (setup-args)))

(defn toggle-quick-filters!
  []
  (swap! db/indirect-holdings update :show-quick-filters? not))

(defn system-views
  []
  [{:named-view/settings {:selected-columns columns/default-columns
                          :ordered-columns  columns/ordered-default-columns
                          :sort-by          [:indirect-holding/fund :asset/name]
                          :sort-direction   "ASC"
                          :view-mode        :grid-mode}
    :named-view/name (loc/tr :all-holdings)
    :disable-delete? true
    :db/id "named-view-all-indirect-holdings"}
   {:named-view/settings {:selected-columns columns/default-columns
                          :ordered-columns  columns/ordered-default-columns
                          :sort-by          [:indirect-holding/fund :asset/name]
                          :sort-direction   "ASC"
                          :view-mode        :board-mode}
    :named-view/name (loc/tr :indirect-holdings-kanban-by-fund)
    :disable-delete? true
    :db/id "named-view-indirect-holdings-kanban"}
  ])

(defn system-views-by-id
  []
  (->> (system-views)
       (map (fn [v]
              [(:db/id v) v]))
       (into {})))

(defn named-views
  []
  (concat (system-views)
          (->> @session/all-named-views
               (cu/only-entity :named-view/view :indirect-holdings)
               (sort-by :named-view/name))))

(defn named-view-by-id
  [id]
  (or (get @session/entities id)
      (get (system-views-by-id) id)))

(defn switch-mode!
  [current-mode]
  (let [new-mode (if (= current-mode :grid-mode) :board-mode :grid-mode)]
    (swap! db/view-settings assoc :view-mode new-mode)
    (ls/update-in (ls-path) assoc :view-mode new-mode)))

(defn load-view!
  [named-view] ;;named-view entity
  (let [{:keys [named-view/settings]} named-view]
    (ls/set-in (ls-path) settings)
    (setup-column-state! settings)))

(defn indirect-holdings-updates
  []
  (let [all-holdings @session/all-holdings
        all-associations @session/all-associations
        all-fof-assets @session/all-fof-assets
        fof-assets-by-id (index-by :db/id all-fof-assets)
        associations-by-entity-id (->> all-associations
                                       (mapcat (fn [a]
                                                 [[(:entity-association/target-entity a) a]
                                                  [(:entity-association/source-entity a) a]]))
                                       (group-by first))
        indirect-holdings
        (->>
         all-holdings
         (mapcat
          (fn [holding]
            (when-let [fof-asset (get fof-assets-by-id (:holding/target-asset holding))]
              (let [fof-asset-id (:db/id fof-asset)]
                (->> (get associations-by-entity-id fof-asset-id)
                     (map second)
                     (cu/filter-by :entity-association/type :association.type/holding)
                     (keep (fn [ass]
                             (let [other-entity-id (associations/other-entity-id ass fof-asset-id)
                                   other-entity (session/get-entity other-entity-id)]
                               (when (assets/asset-type? (:entity/type other-entity))
                                 {:entity/type :entity.type/calculated-indirect-holding
                                  :db/id (:db/id ass)
                                  :indirect-holding/fund fof-asset-id
                                  :indirect-holding/holding-target other-entity-id})))))))))
         (doall))]
    (swap! db/indirect-holdings assoc :grid-entities (vec indirect-holdings))))

(defn permission-updates
  []
  (let [current-permissions @session/current-permissions]
    (when current-permissions
      (precalculated-schema true))))

(defn trigger-recalculation
  []
  (let [permissions-trigger @db/schema
        entities-trigger @db/grid-entities]
    (when (or permissions-trigger entities-trigger)
      (fu/timeout 10
                  (fn []
                    (grid/recalculate (assoc (setup-args) :stale-sort? true))
                    (swap! db/indirect-holdings assoc :loaded true)
                    (deref db/indirect-holdings))))))

(defn setup
  [closed-atom?]
  (when-not @closed-atom?
    (if (empty? (ls/get-in (ls-path)))
      (load-view! (first (named-views)))
      (reset! db/view-settings (ls/get-in (ls-path))))
    (swap! db/indirect-holdings assoc
      :tracker-1 (r/track! indirect-holdings-updates)
      :tracker-2 (r/track! permission-updates)
      :tracker-3 (r/track! trigger-recalculation))
    (setup-column-state! (ls/get-in (ls-path)))))

(defn open-create-indirect-holding-curtain
  []
  (f-entity/open-creator! :entity.type/calculated-indirect-holding {}))

(defn save-current-view!
  [name]
  (let [settings @db/view-settings
        msg {:named-view/name     name
             :named-view/view     :indirect-holdings
             :named-view/settings settings}]
    (named-view-actions/*create-named-view->
     msg
     identity
     (fn [& a] ;;TODO show error message
       (logging/error "create-named-view failed" a)))))

(defn show-collapse-all?
  []
  (let [card-mode (or (:card-mode @db/kanban)
                      :short-card)
        individual-count (count @db/expanded)
        total-entity-count (count @db/sorted-kanban-entities)]
    (or (and (= card-mode :short-card)
             (> individual-count 0))
        (and (= card-mode :long-card)
             (< individual-count total-entity-count)))))

(defn expand-all!
  []
  (swap! db/kanban assoc :card-mode :long-card)
  (reset! db/expanded {}))

(defn collapse-all!
  []
  (swap! db/kanban assoc :card-mode :short-card)
  (reset! db/expanded {}))

(defn open-card-settings!
  []
  (swap! db/kanban assoc :card-settings? true))

(defn close-card-settings!
  []
  (swap! db/kanban dissoc :card-settings?))
